export type FeatureFlagType = 'RELEASE' | 'EXPERIMENT' | 'OPS' | 'PERMISSION' | 'STAGING_ONLY';

export type FeatureFlagDefinition = {
  key: string;
  type: FeatureFlagType;
  description: string;
  createdAt: string;
  expiresAt: string | null;
  owner: string;
  ticket: string | null;
};

export const FeatureFlags: Record<string, FeatureFlagDefinition> = {
  STAGING_ONLY_DIALPLAN_LOGIC: {
    key: 'STAGING_ONLY_DIALPLAN_LOGIC',
    type: 'STAGING_ONLY',
    description: 'Keeps dialplan build and management logic unavailable in production.',
    createdAt: '2026-07-10',
    expiresAt: null,
    owner: 'team-frontend',
    ticket: null,
  },

  // Example (RELEASE flags require expiresAt):
  // RELEASE_NEW_SIDEBAR: {
  //   key: 'RELEASE_NEW_SIDEBAR',
  //   type: 'RELEASE',
  //   description: 'Enables the redesigned sidebar navigation',
  //   createdAt: '2026-05-28',
  //   expiresAt: '2026-06-25',
  //   owner: 'team-frontend',
  //   ticket: 'ENG-1001',
  // },

  // Example (EXPERIMENT flags require expiresAt):
  // EXPERIMENT_TICKETS_LIST_V2: {
  //   key: 'EXPERIMENT_TICKETS_LIST_V2',
  //   type: 'EXPERIMENT',
  //   description: 'Tests a revised tickets list interaction model',
  //   createdAt: '2026-05-28',
  //   expiresAt: '2026-06-18',
  //   owner: 'team-frontend',
  //   ticket: 'ENG-1002',
  // },

  // Example (OPS/PERMISSION flags can use expiresAt: null):
  // OPS_TICKETSYSTEM_KILLSWITCH: {
  //   key: 'OPS_TICKETSYSTEM_KILLSWITCH',
  //   type: 'OPS',
  //   description: 'Emergency switch to disable ticketsystem integration',
  //   createdAt: '2026-05-28',
  //   expiresAt: null,
  //   owner: 'team-platform',
  //   ticket: null,
  // },
};

export type FeatureFlagKey = keyof typeof FeatureFlags;

const flagState: Record<string, boolean> = {};

const isStagingLikeEnvironment = (): boolean => {
  const resolvedEnvironment = [
    import.meta.env.VITE_APP_ENV,
    import.meta.env.VITE_SENTRY_ENVIRONMENT,
    import.meta.env.MODE,
  ].find((value) => typeof value === 'string' && value.trim().length > 0);

  if (!resolvedEnvironment) {
    return false;
  }

  const normalized = resolvedEnvironment.trim().toLowerCase();
  return normalized === 'staging'
    || normalized === 'test'
    || normalized === 'development'
    || normalized === 'dev'
    || normalized === 'local';
};

export const isFeatureEnabled = (flag: string): boolean => {
  const definition = Object.values(FeatureFlags).find((entry) => entry.key === flag);
  if (definition?.type === 'STAGING_ONLY') {
    return isStagingLikeEnvironment();
  }

  return Boolean(flagState[flag]);
};

// Example usage:
// if (isFeatureEnabled(FeatureFlags.RELEASE_NEW_SIDEBAR.key)) {
//   // render/use new sidebar
// }
